import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  memo
} from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaVolumeDown,
  FaExpand,
  FaCompress,
  FaCog,
  FaDownload
} from 'react-icons/fa'
import './VideoPlayer.scss'

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2]

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

const handleAsyncAction = (maybePromise, errorMessage) => {
  if (maybePromise && typeof maybePromise.catch === 'function') {
    maybePromise.catch(err => {
      console.error(errorMessage, err)
    })
  }
}

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.msFullscreenElement ||
  null

const isElementFullscreen = element => {
  const fullscreenElement = getFullscreenElement()

  return !!(
    fullscreenElement &&
    element &&
    (fullscreenElement === element || element.contains(fullscreenElement))
  )
}

const isPlayerInFullscreen = (container, video) => {
  const fullscreenElement = getFullscreenElement()

  if (fullscreenElement && container) {
    return (
      fullscreenElement === container || container.contains(fullscreenElement)
    )
  }

  return !!(video && video.webkitDisplayingFullscreen)
}

const VideoPlayer = forwardRef(
  (
    {
      src,
      poster,
      autoPlay = false,
      width = '100%',
      height = '100%',
      style,
      className,
      onEnded,
      onReady,
      onError,
      showVolume = true,
      controlledVolume
    },
    ref
  ) => {
    const videoRef = useRef(null)
    const containerRef = useRef(null)
    const progressBarRef = useRef(null)
    const controlsTimeoutRef = useRef(null)
    const controlsRafRef = useRef(null)

    const [isPlaying, setIsPlaying] = useState(false)
    const [progress, setProgress] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [isMuted, setIsMuted] = useState(false)
    const [volume, setVolume] = useState(
      controlledVolume !== undefined ? clamp(controlledVolume, 0, 1) : 1
    )
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showControls, setShowControls] = useState(true)
    const [isLoading, setIsLoading] = useState(true)
    const [isDebouncedLoading, setIsDebouncedLoading] = useState(true)
    const [bufferedProgress, setBufferedProgress] = useState(0)
    const [playbackSpeed, setPlaybackSpeed] = useState(1)
    const [isDownloadPending, setIsDownloadPending] = useState(false)
    const [isVideoDownloading, setIsVideoDownloading] = useState(false)

    const [isScrubbing, setIsScrubbing] = useState(false)
    const [scrubbingTime, setScrubbingTime] = useState(null)

    const [loadedBytes, setLoadedBytes] = useState(0)
    const [totalBytes, setTotalBytes] = useState(0)
    const [downloadSpeed, setDownloadSpeed] = useState(0)

    const lastLoadedRef = useRef(0)
    const lastLoadTimeRef = useRef(Date.now())
    const lastUiUpdateRef = useRef({ currentTime: 0, progress: 0 })
    const lastBufferedProgressRef = useRef(0)
    const lastRenderedLoadedBytesRef = useRef(0)
    const lastRenderedSpeedRef = useRef(0)
    const isPlayingRef = useRef(false)
    const isScrubbingRef = useRef(false)
    const isHoveringRef = useRef(false)
    const showControlsRef = useRef(true)
    const wasPlayingBeforeScrubRef = useRef(false)

    const [showSettings, setShowSettings] = useState(false)
    const [showStatsInSettings, setShowStatsInSettings] = useState(false)

    useEffect(() => {
      const video = videoRef.current
      if (!video) return

      setIsLoading(true)
      setLoadedBytes(0)
      setTotalBytes(0)
      setBufferedProgress(0)
      setDownloadSpeed(0)
      setIsDownloadPending(false)
      setIsVideoDownloading(false)

      lastLoadedRef.current = 0
      lastRenderedLoadedBytesRef.current = 0
      lastRenderedSpeedRef.current = 0
      lastLoadTimeRef.current = Date.now()
      lastUiUpdateRef.current = { currentTime: 0, progress: 0 }
      lastBufferedProgressRef.current = 0

      if (autoPlay) {
        video.play().catch(() => {
          setIsMuted(true)
          video.muted = true
          video.play().catch(e => console.error('Autoplay failed:', e))
        })
      }
    }, [autoPlay, src])

    useEffect(() => {
      if (!src || !showStatsInSettings) return

      const controller = new AbortController()

      fetch(src, { method: 'HEAD', signal: controller.signal })
        .then(response => {
          const contentLength = response.headers.get('Content-Length')
          if (contentLength) {
            setTotalBytes(parseInt(contentLength, 10))
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.warn('Could not fetch video size:', err)
          }
        })

      return () => {
        controller.abort()
      }
    }, [showStatsInSettings, src])

    useEffect(() => {
      if (controlledVolume !== undefined && videoRef.current) {
        const newVol = clamp(controlledVolume, 0, 1)
        const shouldMute = newVol === 0

        setVolume(newVol)
        setIsMuted(shouldMute)

        videoRef.current.volume = newVol
        videoRef.current.muted = shouldMute
      }
    }, [controlledVolume])

    useEffect(() => {
      let timeout

      if (isLoading) {
        timeout = setTimeout(() => {
          setIsDebouncedLoading(true)
        }, 300)
      } else {
        setIsDebouncedLoading(false)
      }

      return () => clearTimeout(timeout)
    }, [isLoading])

    useEffect(() => {
      isPlayingRef.current = isPlaying
    }, [isPlaying])

    useEffect(() => {
      isScrubbingRef.current = isScrubbing
    }, [isScrubbing])

    useEffect(() => {
      showControlsRef.current = showControls
    }, [showControls])

    useEffect(() => {
      const handleFullscreenChange = () => {
        const nextIsFullscreen = isPlayerInFullscreen(
          containerRef.current,
          videoRef.current
        )

        setIsFullscreen(prev =>
          prev === nextIsFullscreen ? prev : nextIsFullscreen
        )

        if (!nextIsFullscreen) {
          setShowControls(true)
        }
      }

      const video = videoRef.current

      document.addEventListener('fullscreenchange', handleFullscreenChange)
      document.addEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      )

      if (video) {
        video.addEventListener('webkitbeginfullscreen', handleFullscreenChange)
        video.addEventListener('webkitendfullscreen', handleFullscreenChange)
      }

      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange)
        document.removeEventListener(
          'webkitfullscreenchange',
          handleFullscreenChange
        )

        if (video) {
          video.removeEventListener(
            'webkitbeginfullscreen',
            handleFullscreenChange
          )
          video.removeEventListener(
            'webkitendfullscreen',
            handleFullscreenChange
          )
        }
      }
    }, [])

    useEffect(() => {
      const handleGlobalMouseUp = () => {
        if (isScrubbing) {
          handleScrubEnd()
        }
      }

      const handleGlobalMouseMove = e => {
        if (isScrubbing) {
          if (e.cancelable) {
            e.preventDefault()
          }

          handleScrubMove(e)
        }
      }

      if (isScrubbing) {
        const touchMoveOptions = { passive: false }

        document.addEventListener('mouseup', handleGlobalMouseUp)
        document.addEventListener('touchend', handleGlobalMouseUp)
        document.addEventListener('mousemove', handleGlobalMouseMove)
        document.addEventListener(
          'touchmove',
          handleGlobalMouseMove,
          touchMoveOptions
        )
      }

      return () => {
        const touchMoveOptions = { passive: false }

        document.removeEventListener('mouseup', handleGlobalMouseUp)
        document.removeEventListener('touchend', handleGlobalMouseUp)
        document.removeEventListener('mousemove', handleGlobalMouseMove)
        document.removeEventListener(
          'touchmove',
          handleGlobalMouseMove,
          touchMoveOptions
        )
      }
    }, [isScrubbing])

    const togglePlay = useCallback(() => {
      const video = videoRef.current
      if (!video) return

      if (video.paused) {
        video.play().catch(e => console.error('Play failed:', e))
      } else {
        video.pause()
      }
    }, [])

    const handleTimeUpdate = () => {
      const video = videoRef.current
      if (!video || isScrubbing) return

      const current = video.currentTime
      const dur = video.duration || 0
      const nextProgress = dur > 0 ? (current / dur) * 100 : 0

      const shouldSyncTime =
        Math.abs(current - lastUiUpdateRef.current.currentTime) >= 0.1 ||
        current === 0 ||
        current >= dur

      const shouldSyncProgress =
        Math.abs(nextProgress - lastUiUpdateRef.current.progress) >= 0.25 ||
        nextProgress === 0 ||
        nextProgress >= 100

      if (shouldSyncTime) {
        lastUiUpdateRef.current.currentTime = current
        setCurrentTime(current)
      }

      if (dur > 0 && shouldSyncProgress) {
        lastUiUpdateRef.current.progress = nextProgress
        setProgress(nextProgress)
      }
    }

    const handleLoadedMetadata = () => {
      const video = videoRef.current
      if (!video) return

      video.playbackRate = playbackSpeed
      video.volume = volume
      video.muted = isMuted

      setDuration(video.duration)

      if (onReady) onReady()
    }

    const handleLoadStart = () => {
      setIsLoading(true)
    }

    const handleLoadedData = () => {
      const video = videoRef.current

      if (video && video.readyState >= 2) {
        setIsLoading(false)
      }
    }

    const handleCanPlay = () => {
      if (videoRef.current && videoRef.current.readyState >= 2) {
        setIsLoading(false)
      }
    }

    const handleCanPlayThrough = () => {
      setIsLoading(false)
    }

    const handleProgress = () => {
      const video = videoRef.current
      if (!video || !video.duration) return

      let maxBufferedEnd = 0

      for (let i = 0; i < video.buffered.length; i++) {
        if (video.buffered.end(i) > maxBufferedEnd) {
          maxBufferedEnd = video.buffered.end(i)
        }
      }

      if (maxBufferedEnd > 0) {
        const percent = Math.min(100, (maxBufferedEnd / video.duration) * 100)

        if (Math.abs(percent - lastBufferedProgressRef.current) >= 0.5) {
          lastBufferedProgressRef.current = percent
          setBufferedProgress(percent)
        }

        if (showStatsInSettings && totalBytes > 0) {
          const currentLoadedBytes = (percent / 100) * totalBytes
          const minimumVisibleDelta = Math.max(totalBytes * 0.005, 64 * 1024)

          const now = Date.now()
          const timeDiff = (now - lastLoadTimeRef.current) / 1000
          const bytesDiff = currentLoadedBytes - lastLoadedRef.current

          if (timeDiff >= 1 && bytesDiff > 0) {
            const speedKbps = (bytesDiff * 8) / 1024 / timeDiff
            const nextSpeed =
              lastRenderedSpeedRef.current === 0
                ? speedKbps
                : lastRenderedSpeedRef.current * 0.7 + speedKbps * 0.3

            if (
              Math.abs(nextSpeed - lastRenderedSpeedRef.current) >= 25 ||
              percent >= 100
            ) {
              lastRenderedSpeedRef.current = nextSpeed
              setDownloadSpeed(nextSpeed)
            }

            lastLoadedRef.current = currentLoadedBytes
            lastLoadTimeRef.current = now
            setIsDownloadPending(false)
          } else if (timeDiff >= 1 && bytesDiff === 0 && percent < 100) {
            setIsDownloadPending(true)
          }

          if (
            Math.abs(currentLoadedBytes - lastRenderedLoadedBytesRef.current) >=
            minimumVisibleDelta ||
            percent >= 100
          ) {
            lastRenderedLoadedBytesRef.current = currentLoadedBytes
            setLoadedBytes(currentLoadedBytes)
          }
        }
      }
    }

    const handleWaiting = () => {
      const video = videoRef.current

      if (video) {
        const currentVideoTime = video.currentTime
        let isBuffered = false

        for (let i = 0; i < video.buffered.length; i++) {
          if (
            currentVideoTime >= video.buffered.start(i) &&
            currentVideoTime <= video.buffered.end(i) + 0.1
          ) {
            isBuffered = true
            break
          }
        }

        if (!isBuffered || video.readyState < 2) {
          setIsLoading(true)
        }
      } else {
        setIsLoading(true)
      }
    }

    const handlePlaying = () => {
      const video = videoRef.current

      if (video && video.readyState >= 2) {
        setIsLoading(false)
      }
    }

    const handleSeeking = () => {
      const video = videoRef.current
      if (!video) return

      setIsLoading(true)
    }

    const handleSeeked = () => {
      const video = videoRef.current
      if (!video) return

      const currentVideoTime = video.currentTime
      let isBuffered = false

      for (let i = 0; i < video.buffered.length; i++) {
        if (
          currentVideoTime >= video.buffered.start(i) &&
          currentVideoTime <= video.buffered.end(i)
        ) {
          isBuffered = true
          break
        }
      }

      if (isBuffered && video.readyState >= 2) {
        setIsLoading(false)
      } else {
        setIsLoading(true)
      }
    }

    const handleStalled = () => {
      console.warn('Video playback stalled')
    }

    const handleVideoError = e => {
      setIsLoading(false)
      console.error('Video error event:', e)

      if (onError) onError(e)
    }

    const handleScrubStart = e => {
      e.stopPropagation()
      e.preventDefault()

      const video = videoRef.current
      if (!video || !video.duration) return

      setIsScrubbing(true)
      wasPlayingBeforeScrubRef.current = !video.paused

      if (!video.paused) {
        video.pause()
      }

      const newTime = calculateSeekTime(e)

      if (newTime !== null) {
        seekToTime(newTime)
      }
    }

    const handleScrubMove = e => {
      if (!isScrubbing) return

      const newTime = calculateSeekTime(e)

      if (newTime !== null) {
        seekToTime(newTime)
      }
    }

    const calculateSeekTime = e => {
      const progressBar = progressBarRef.current
      const video = videoRef.current

      if (!progressBar || !video || !video.duration) return null

      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX)
      if (clientX === undefined) return null

      const rect = progressBar.getBoundingClientRect()
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left))
      const percentage = x / rect.width

      return percentage * video.duration
    }

    const seekToTime = time => {
      const video = videoRef.current
      if (!video || !video.duration) return

      const clampedTime = clamp(time, 0, video.duration)
      const percentage = (clampedTime / video.duration) * 100

      setScrubbingTime(prev =>
        prev === null || Math.abs(prev - clampedTime) >= 0.05
          ? clampedTime
          : prev
      )

      setProgress(prev =>
        Math.abs(prev - percentage) >= 0.1 ? percentage : prev
      )

      setCurrentTime(prev =>
        Math.abs(prev - clampedTime) >= 0.05 ? clampedTime : prev
      )

      if (video.seekable.length > 0) {
        const seekableStart = video.seekable.start(0)
        const seekableEnd = video.seekable.end(video.seekable.length - 1)
        video.currentTime = clamp(clampedTime, seekableStart, seekableEnd)
      } else {
        video.currentTime = clampedTime
      }
    }

    const handleScrubEnd = () => {
      if (!isScrubbing) return

      const video = videoRef.current
      if (!video) return

      setIsScrubbing(false)
      setScrubbingTime(null)

      if (wasPlayingBeforeScrubRef.current) {
        video.play().catch(e => console.error('Resume failed:', e))
      }
    }

    const toggleMute = () => {
      const video = videoRef.current
      if (!video) return

      video.muted = !video.muted
      setIsMuted(!isMuted)
    }

    const handleVolumeChange = e => {
      const video = videoRef.current
      if (!video) return

      const newVolume = clamp(parseFloat(e.target.value), 0, 1)

      video.volume = newVolume
      setVolume(newVolume)

      if (newVolume === 0) {
        setIsMuted(true)
        video.muted = true
      } else if (isMuted) {
        setIsMuted(false)
        video.muted = false
      }
    }

    const toggleSettings = () => {
      setShowSettings(prev => !prev)
    }

    const getDownloadFileName = () => {
      if (!src) return 'video.mp4'

      try {
        const cleanUrl = src.split('?')[0]
        const fileName = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1)

        if (!fileName) return 'video.mp4'

        return fileName.includes('.') ? fileName : `${fileName}.mp4`
      } catch {
        return 'video.mp4'
      }
    }

    const handleDownloadVideo = async () => {
      if (!src || isVideoDownloading) return

      setIsVideoDownloading(true)

      try {
        const response = await fetch(src)

        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`)
        }

        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = blobUrl
        link.download = getDownloadFileName()

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.warn('Direct video download failed, opening URL instead:', error)

        const link = document.createElement('a')
        link.href = src
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        link.download = getDownloadFileName()
        link.click()
      } finally {
        setIsVideoDownloading(false)
      }
    }

    const toggleFullscreen = () => {
      const container = containerRef.current
      const video = videoRef.current

      if (!container || !video) return

      const isCurrentPlayerFullscreen = isPlayerInFullscreen(container, video)

      if (!isCurrentPlayerFullscreen) {
        if (container.requestFullscreen) {
          handleAsyncAction(
            container.requestFullscreen(),
            'Error attempting to enable fullscreen:'
          )
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen()
        } else if (video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen()
        }
      } else {
        if (isElementFullscreen(container)) {
          if (document.exitFullscreen) {
            handleAsyncAction(
              document.exitFullscreen(),
              'Error attempting to exit fullscreen:'
            )
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen()
          }
        } else if (
          video.webkitDisplayingFullscreen &&
          video.webkitExitFullscreen
        ) {
          video.webkitExitFullscreen()
        }
      }
    }

    const changePlaybackSpeed = () => {
      const video = videoRef.current
      if (!video) return

      const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed)
      const nextSpeed =
        PLAYBACK_SPEEDS[(currentIndex + 1) % PLAYBACK_SPEEDS.length]

      video.playbackRate = nextSpeed
      setPlaybackSpeed(nextSpeed)
    }

    const formatTime = time => {
      if (!time || isNaN(time)) return '00:00'

      const minutes = Math.floor(time / 60)
      const seconds = Math.floor(time % 60)

      return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`
    }

    const formatBytes = bytes => {
      if (bytes === 0) return '0 MB'

      const mb = bytes / (1024 * 1024)

      if (mb >= 1) {
        return `${mb.toFixed(1)} MB`
      }

      const kb = bytes / 1024
      return `${kb.toFixed(0)} KB`
    }

    const formatSpeed = kbps => {
      if (kbps === 0) return '0 Kbps'

      if (kbps >= 1024) {
        return `${(kbps / 1024).toFixed(1)} Mbps`
      }

      return `${kbps.toFixed(0)} Kbps`
    }

    const clearControlsTimeout = useCallback(() => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
        controlsTimeoutRef.current = null
      }
    }, [])

    const scheduleControlsHide = useCallback(
      delay => {
        clearControlsTimeout()

        if (!isPlayingRef.current || isScrubbingRef.current) return

        controlsTimeoutRef.current = setTimeout(() => {
          if (!isHoveringRef.current) {
            setShowControls(false)
          }
        }, delay)
      },
      [clearControlsTimeout]
    )

    useImperativeHandle(ref, () => ({
      play: () => videoRef.current?.play(),
      pause: () => videoRef.current?.pause(),
      togglePlay,
      toggleFullscreen,
      seekTo: seekToTime,
      getVideoElement: () => videoRef.current
    }))

    const handleMouseEnter = () => {
      isHoveringRef.current = true
      setShowControls(true)
      clearControlsTimeout()
    }

    const handleMouseLeave = () => {
      isHoveringRef.current = false
      scheduleControlsHide(2000)
    }

    const handleMouseMove = () => {
      if (!isPlayingRef.current && showControlsRef.current) return
      if (controlsRafRef.current) return

      controlsRafRef.current = window.requestAnimationFrame(() => {
        controlsRafRef.current = null

        if (!showControlsRef.current) {
          setShowControls(true)
        }

        scheduleControlsHide(3000)
      })
    }

    const handlePause = () => {
      setIsPlaying(false)
      setShowControls(true)
      clearControlsTimeout()
    }

    const handlePlay = () => {
      setIsPlaying(true)
      scheduleControlsHide(3000)
    }

    useEffect(() => {
      return () => {
        clearControlsTimeout()

        if (controlsRafRef.current) {
          window.cancelAnimationFrame(controlsRafRef.current)
        }
      }
    }, [clearControlsTimeout])

    useEffect(() => {
      const handleClickOutside = e => {
        if (
          showSettings &&
          !e.target.closest('.shl-settings-menu') &&
          !e.target.closest('.shl-control-btn')
        ) {
          setShowSettings(false)
        }
      }

      if (showSettings) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [showSettings])

    useEffect(() => {
      if (!showStatsInSettings) return undefined

      const checkDownloadActivity = setInterval(() => {
        const now = Date.now()
        const timeSinceLastUpdate = (now - lastLoadTimeRef.current) / 1000

        if (timeSinceLastUpdate > 2 && lastRenderedSpeedRef.current !== 0) {
          lastRenderedSpeedRef.current = 0
          setDownloadSpeed(0)
        }
      }, 1000)

      return () => clearInterval(checkDownloadActivity)
    }, [showStatsInSettings])

    return (
      <div
        ref={containerRef}
        className={classNames('shl-video-player', className, {
          'is-fullscreen': isFullscreen,
          'hide-cursor': isPlaying && !showControls && !isScrubbing,
          'settings-open': showSettings
        })}
        style={{ width, height, ...style }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onContextMenu={e => e.preventDefault()}
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          className="shl-video-element"
          onClick={togglePlay}
          onDoubleClick={toggleFullscreen}
          controls={false}
          controlsList="nodownload"
          onLoadStart={handleLoadStart}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={handleLoadedData}
          onCanPlay={handleCanPlay}
          onProgress={handleProgress}
          onSeeking={handleSeeking}
          onSeeked={handleSeeked}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={onEnded}
          onWaiting={handleWaiting}
          onPlaying={handlePlaying}
          onStalled={handleStalled}
          onCanPlayThrough={handleCanPlayThrough}
          onError={handleVideoError}
          playsInline
          preload="metadata"
        />

        {!isPlaying && !isLoading && (
          <div className="shl-video-overlay-center" onClick={togglePlay}>
            <div className="shl-play-button-large">
              <FaPlay className="shl-icon-play" />
            </div>
          </div>
        )}

        {isDebouncedLoading && (
          <div className="shl-video-loader">
            <div className="shl-spinner"></div>
          </div>
        )}

        <div
          className={classNames('shl-video-controls', {
            'is-visible': showControls || !isPlaying || isScrubbing
          })}
        >
          <div
            className="shl-progress-container"
            ref={progressBarRef}
            onMouseDown={handleScrubStart}
            onTouchStart={handleScrubStart}
          >
            <div className="shl-progress-track">
              <div
                className="shl-progress-buffer"
                style={{ width: `${bufferedProgress}%` }}
              />
              <div
                className="shl-progress-fill"
                style={{ width: `${progress}%` }}
              />
              <div
                className="shl-progress-thumb"
                style={{ left: `${progress}%` }}
              />
            </div>

            {isScrubbing && scrubbingTime !== null && (
              <div
                className="shl-scrub-tooltip"
                style={{ left: `${(scrubbingTime / duration) * 100}%` }}
              >
                {formatTime(scrubbingTime)}
              </div>
            )}
          </div>

          <div className="shl-controls-row">
            <div className="shl-controls-left">
              <button className="shl-control-btn" onClick={togglePlay}>
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>

              <div className="shl-time-display">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>

            <div className="shl-controls-right">
              <button
                className="shl-control-btn shl-speed-btn"
                onClick={changePlaybackSpeed}
                title="Playback speed"
              >
                <span className="shl-speed-text">{playbackSpeed}x</span>
              </button>

              {showVolume && (
                <div className="shl-volume-control">
                  <button className="shl-control-btn" onClick={toggleMute}>
                    {isMuted || volume === 0 ? (
                      <FaVolumeMute />
                    ) : volume < 0.5 ? (
                      <FaVolumeDown />
                    ) : (
                      <FaVolumeUp />
                    )}
                  </button>

                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="shl-volume-slider"
                  />
                </div>
              )}

              <button
                className={classNames('shl-control-btn', {
                  'is-active': showSettings
                })}
                onClick={toggleSettings}
                title="Settings"
              >
                <FaCog />
              </button>

              <button className="shl-control-btn" onClick={toggleFullscreen}>
                {isFullscreen ? <FaCompress /> : <FaExpand />}
              </button>
            </div>
          </div>

          {showSettings && (
            <div className="shl-settings-menu">
              <div className="shl-settings-header">
                <span>Settings</span>
              </div>

              <button
                type="button"
                className="shl-settings-action"
                onClick={handleDownloadVideo}
                disabled={isVideoDownloading}
              >
                <FaDownload />
                <span>
                  {isVideoDownloading ? 'Preparing download...' : 'Download Video'}
                </span>
              </button>

              <div className="shl-settings-item">
                <label className="shl-settings-label">
                  <input
                    type="checkbox"
                    checked={showStatsInSettings}
                    onChange={e => setShowStatsInSettings(e.target.checked)}
                    className="shl-settings-checkbox"
                  />
                  <span>Show Network Stats</span>
                </label>
              </div>

              {showStatsInSettings && (
                <div className="shl-settings-stats">
                  <div className="shl-stats-row">
                    <span className="shl-stats-label">Download Speed:</span>
                    <span className="shl-stats-value">
                      {isDownloadPending ? (
                        <div className="shl-mini-spinner-inline"></div>
                      ) : downloadSpeed > 0 ? (
                        formatSpeed(downloadSpeed)
                      ) : loadedBytes >= totalBytes && totalBytes > 0 ? (
                        'Complete'
                      ) : (
                        'Idle'
                      )}
                    </span>
                  </div>

                  <div className="shl-stats-row">
                    <span className="shl-stats-label">Loaded / Total:</span>
                    <span className="shl-stats-value">
                      {totalBytes > 0
                        ? `${formatBytes(loadedBytes)} / ${formatBytes(
                          totalBytes
                        )}`
                        : 'Calculating...'}
                    </span>
                  </div>

                  <div className="shl-stats-row">
                    <span className="shl-stats-label">Buffer:</span>
                    <span className="shl-stats-value">
                      {bufferedProgress.toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
)

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  autoPlay: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  className: PropTypes.string,
  onEnded: PropTypes.func,
  onReady: PropTypes.func,
  onError: PropTypes.func,
  showVolume: PropTypes.bool,
  controlledVolume: PropTypes.number
}

VideoPlayer.displayName = 'VideoPlayer'

const MemoizedVideoPlayer = memo(VideoPlayer)

MemoizedVideoPlayer.displayName = 'VideoPlayer'
MemoizedVideoPlayer.propTypes = VideoPlayer.propTypes

export default MemoizedVideoPlayer